import { t, Trans } from '@lingui/macro';
import React from 'react';
import styled from 'styled-components';

import { PrivacySettingsColors } from './colors';
import PasskeyCopy from './PasskeyCopy';
import { SuccessMessageProps } from './types';

// ===== STYLED COMPONENTS =====

const SuccessMessageWrapper = styled.div`
  background-color: ${PrivacySettingsColors.SuccessBackground};
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  font-size: 14px;
`;

const SuccessHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  font-weight: 500;
  color: ${PrivacySettingsColors.SuccessTextDark};

  .bff-check-circle-filled {
    color: ${PrivacySettingsColors.SuccessText};
    margin-right: 8px;
    font-size: 20px;
  }
`;

const CheckIcon = styled.span`
  color: ${PrivacySettingsColors.SuccessText};
  margin-right: 8px;
  font-size: 18px;
`;

const PasskeyDisplay = styled.div`
  margin-bottom: 8px;
  font-size: 13px;
  color: ${PrivacySettingsColors.VeryDarkText};

  > span {
    margin-right: 8px;
  }
`;

const PasskeyControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const PasskeyInput = styled.input`
  background: transparent;
  border: none;
  font-size: 13px;
  width: 300px;
  height: 30px;
  outline: none;

  &:disabled {
    background-color: ${PrivacySettingsColors.LightGrayBackground};
    color: ${PrivacySettingsColors.MutedText};
    cursor: not-allowed;
    opacity: 0.65;
  }

  &:hover {
      border: none;
      box-shadow: none;
      outline: none;
    }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  color: ${PrivacySettingsColors.DarkText};

  &:hover {
    opacity: 0.7;
  }
`;

const ExpirationInfo = styled.div`
  font-size: 13px;
  color: ${PrivacySettingsColors.VeryDarkText};
  margin-bottom: 8px;
`;

const ChangePasskeyButton = styled.button`
  background: none;
  border: none;
  color: ${PrivacySettingsColors.PurplePopsicle};
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  padding: 5px 0 0 0;

  &:hover {
    text-decoration: none;
  }
`;

// ===== MAIN COMPONENT =====

/**
 * SuccessMessage Component
 * Displays success message after passkey update
 * Shows confirmation with current passkey display and change option
 */
const SuccessMessage: React.FunctionComponent<SuccessMessageProps> = ({
  passkey,
  expiryDate,
  showCurrentPasskey,
  showNewPasskeyField,
  onToggleVisibility,
  onChangePasskey
}) => {
  /**
   * Formats passkey for display (visible or hidden with dots)
   */
  const formatPasskey = (passkeyValue: string): string => {
    return showCurrentPasskey ? passkeyValue : '•'.repeat(passkeyValue.length);
  };

  return (
    <SuccessMessageWrapper data-testid="success-message-wrapper">
      <SuccessHeader>
        <CheckIcon className="bff-check-circle-filled" />
        <Trans>Passkey changes have been saved.</Trans>
      </SuccessHeader>

      <PasskeyDisplay data-testid="passkey-display">
        <span><Trans>View Current Passkey:</Trans></span>
        <PasskeyControls data-testid="passkey-controls">
          <PasskeyInput
            readOnly
            type="text"
            value={formatPasskey(passkey)}
          />
          <IconButton
            onClick={onToggleVisibility}
            title={showCurrentPasskey ? t`Hide passkey` : t`Show passkey`}
            type="button"
          >
            <span className={showCurrentPasskey ? 'bff-hide' : 'bff-show'} />
          </IconButton>
          <PasskeyCopy passkey={passkey} />
        </PasskeyControls>
      </PasskeyDisplay>

      <ExpirationInfo>
        <Trans>Expiration date:</Trans> <em>{expiryDate || t`No expiration date set`}</em>
      </ExpirationInfo>

      {!showNewPasskeyField && (
        <ChangePasskeyButton onClick={onChangePasskey} type="button">
          <Trans>Change Passkey</Trans>
        </ChangePasskeyButton>
      )}
    </SuccessMessageWrapper>
  );
};

export default SuccessMessage;
